import { useState } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';
import { MapPin, ChevronDown, Layers, Check } from 'lucide-react';

export const GlobalLocationFilter = () => {
  const [open, setOpen] = useState(false);
  const {
    locations,
    segments,
    selectedLocations,
    setSelectedLocations,
    getSelectedLocationNames
  } = useLocation();

  const [tempSelection, setTempSelection] = useState([]);

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setTempSelection([...selectedLocations]);
    }
    setOpen(isOpen);
  };

  const handleApply = () => {
    setSelectedLocations(tempSelection);
    setOpen(false);
  };

  const selectSegmentTemp = (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (segment) {
      setTempSelection(segment.location_ids || []);
    }
  };

  const selectAllTemp = () => {
    const filteredLocations = locations.filter(l => l.code !== 'OPTSC_HQ');
    setTempSelection(filteredLocations.map(loc => loc.id));
  };

  const filteredLocations = locations.filter(l => l.code !== 'OPTSC_HQ');
  const allSelected = tempSelection.length === filteredLocations.length && filteredLocations.length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 min-w-[180px] justify-between"
          data-testid="location-filter-trigger"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="font-medium">{getSelectedLocationNames()}</span>
          </div>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <h4 className="font-semibold text-sm">Filter by Franchise</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Select a franchise or all locations
          </p>
        </div>

        {/* All Locations option */}
        <div className="p-2">
          <button
            onClick={selectAllTemp}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm hover:bg-muted transition-colors ${allSelected ? 'bg-primary/10 text-primary' : ''}`}
            data-testid="franchise-all"
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span className="font-medium">All Locations</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{filteredLocations.length} locations</Badge>
              {allSelected && <Check className="w-4 h-4 text-primary" />}
            </div>
          </button>
        </div>

        {/* Franchise Section */}
        {segments.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <p className="text-xs font-medium text-muted-foreground px-2 mb-2">FRANCHISES</p>
              <div className="space-y-1">
                {segments.map((segment) => {
                  const isTempSelected = segment.location_ids &&
                    segment.location_ids.length > 0 &&
                    segment.location_ids.every(id => tempSelection.includes(id)) &&
                    tempSelection.length === segment.location_ids.length;

                  return (
                    <button
                      key={segment.id}
                      onClick={() => selectSegmentTemp(segment.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm hover:bg-muted transition-colors ${isTempSelected ? 'bg-primary/10 text-primary' : ''}`}
                      data-testid={`franchise-${segment.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        <span>{segment.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {segment.location_ids?.length || 0} locations
                        </Badge>
                        {isTempSelected && <Check className="w-4 h-4 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Summary and Apply */}
        <Separator />
        <div className="p-3 bg-muted/30 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Selected:</span>
            <Badge variant={allSelected ? 'default' : 'secondary'}>
              {allSelected ? 'All Locations' : `${tempSelection.length} of ${filteredLocations.length}`}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleApply}
              className="flex-1"
              size="sm"
              data-testid="apply-location-filter"
            >
              Apply
            </Button>
            <Button
              onClick={() => { selectAllTemp(); handleApply(); }}
              variant="outline"
              size="sm"
              className="flex-1"
              data-testid="clear-location-filter"
            >
              Clear Filter
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GlobalLocationFilter;
